import { getTeableConfig, isTeableConfigured } from '@/config/teableConfig';
import type { TeableResponse } from '@/types/teable';
import { rateLimiter, RATE_LIMIT_CONFIGS } from '@/utils/rateLimiter';
import { sanitizeInput } from '@/utils/inputValidation';
import { validateHttpsUrl } from '@/utils/httpsEnforcer';
import { secureLog, sanitizeErrorMessage } from '@/utils/secureLogger';

import { supabase } from '@/integrations/supabase/client';

/**
 * Chiamata sicura alle API Teable tramite Edge Function proxy
 */
const directFetch = async (endpoint: string, options: {
  method: string;
  body?: any;
  params?: Record<string, any>;
}) => {
  secureLog.log('🚀 [directFetch] Chiamata a Teable tramite proxy:', { endpoint, method: options.method });
  
  try {
    const { data, error } = await supabase.functions.invoke('teable-proxy', {
      body: {
        method: options.method,
        endpoint,
        body: options.body,
        params: options.params
      }
    });

    if (error) {
      secureLog.error('❌ [directFetch] Errore proxy:', error);
      throw new Error(error.message || 'Errore nella richiesta');
    }

    secureLog.log('✅ [directFetch] Dati ricevuti:', data);
    return data;
  } catch (error) {
    secureLog.error('💥 [directFetch] Errore:', error);
    throw error;
  }
};

/**
 * Crea le headers per le richieste (ora inutilizzato, mantenuto per compatibilità)
 * @deprecated Usare directFetch invece
 */
export const getHeaders = () => {
  return {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  };
};

/**
 * Gestisce gli errori delle richieste HTTP in modo sicuro
 * Non espone dettagli tecnici sensibili all'utente
 */
const handleResponse = async (response: Response) => {
  if (!response.ok) {
    // Log dettagliato solo in console (non visibile all'utente finale)
    secureLog.error(`Errore HTTP ${response.status}`);
    
    // Messaggi utente-friendly senza dettagli tecnici
    let userMessage = 'Si è verificato un errore nella richiesta';
    
    if (response.status === 401) {
      userMessage = 'Autorizzazione non valida';
    } else if (response.status === 403) {
      userMessage = 'Accesso negato';
    } else if (response.status === 404) {
      userMessage = 'Risorsa non trovata';
    } else if (response.status === 429) {
      userMessage = 'Troppe richieste. Riprova tra qualche istante';
    } else if (response.status >= 500) {
      userMessage = 'Errore del server. Riprova più tardi';
    }
    
    throw new Error(userMessage);
  }
  
  const data = await response.json();
  return data;
};

/**
 * Ottiene l'ID di una tabella per nome promozione dalla tabella Promozioni
 */
export const getPromoTableId = async (promoName: string): Promise<string> => {
  if (!isTeableConfigured()) {
    throw new Error('Teable non configurato');
  }

  secureLog.log('🔍 Ricerca ID tabella per promozione:', promoName);
  
  try {
    // Recupera la promozione dalla tabella principale per ottenere il TableId
    const config = getTeableConfig();
    const promos = await getTableRecords(config.promoTableId, {
      filter: {
        fieldId: 'Nome',
        operator: 'is',
        value: promoName
      },
      maxRecords: 1
    });
    
    if (!promos || promos.length === 0) {
      secureLog.error('❌ Promozione non trovata:', promoName);
      throw new Error(`Promozione "${promoName}" non trovata nella tabella Promozioni`);
    }
    
    const promo = promos[0];
    const tableId = promo.fields?.TableId;
    
    if (!tableId) {
      secureLog.error('❌ TableId non configurato per la promozione:', promoName);
      throw new Error(`TableId mancante per "${promoName}". Aggiungi l'ID della tabella nel campo TableId della promozione.`);
    }
    
    secureLog.log('✅ ID tabella trovato:', tableId);
    return tableId;
    
  } catch (error) {
    secureLog.error('💥 Errore recupero ID tabella:', error);
    throw error;
  }
};

/**
 * Ottiene tutti i record di una tabella con paginazione automatica
 * URL corretto: {baseUrl}/table/{tableId}/record
 */
export const getTableRecords = async (tableId: string, options?: {
  filter?: any;
  sort?: Array<{ field: string; order: 'asc' | 'desc' }>;
  maxRecords?: number;
  offset?: number; // Nuovo parametro per specificare l'offset di partenza
}): Promise<any[]> => {
  if (!isTeableConfigured()) {
    throw new Error('Teable non configurato');
  }

  const config = getTeableConfig();
  
  // Se viene fornito un offset specifico, usa una logica semplificata
  if (options?.offset !== undefined) {
    const maxRecords = options?.maxRecords || 100;
    
    // Prepara i parametri per la richiesta
    const params: Record<string, any> = {
      fieldKeyType: 'name',
      cellFormat: 'text',
      offset: options.offset,
      maxRecords
    };
    
    // Aggiungi parametri di query se forniti
    if (options?.filter) {
      params.filter = {
        conjunction: 'and',
        filterSet: [options.filter]
      };
    }
    
    if (options?.sort) {
      params.sort = options.sort;
    } else {
      params.sort = [{ field: 'Posizione', order: 'asc' }];
    }

    secureLog.log(`📡 Richiesta diretta con offset ${options.offset}`);
    
    try {
      const data = await directFetch(`/table/${tableId}/record`, {
        method: 'GET',
        params
      });
    const records = data.records || [];
    
    secureLog.log(`📦 Records ricevuti con offset: ${records.length}`);
    return records;
    
  } catch (error) {
    secureLog.error('💥 Errore recupero records:', sanitizeErrorMessage(error));
    throw new Error(sanitizeErrorMessage(error));
  }
  }
  
  // Logica originale per paginazione completa
  let allRecords: any[] = [];
  let offset = 0;
  const pageSize = 100; // Teable ha un limite di 100 record per pagina
  const requestedMaxRecords = options?.maxRecords || 50000; // Default alto per recuperare tutti i record
  
  secureLog.log(`📡 Inizio recupero records con limite: ${requestedMaxRecords}`);

  while (allRecords.length < requestedMaxRecords) {
    // Calcola quanti record rimanenti richiedere
    const remainingRecords = requestedMaxRecords - allRecords.length;
    const currentPageSize = Math.min(pageSize, remainingRecords);
    
    // Prepara i parametri per la richiesta
    const params: Record<string, any> = {
      fieldKeyType: 'name',
      cellFormat: 'text',
      maxRecords: currentPageSize
    };
    
    if (offset > 0) {
      params.offset = offset;
    }
    
    // Aggiungi parametri di query se forniti
    if (options?.filter) {
      params.filter = {
        conjunction: 'and',
        filterSet: [options.filter]
      };
    }
    
    if (options?.sort) {
      params.sort = options.sort;
    } else {
      params.sort = [{ field: 'Posizione', order: 'asc' }];
    }

    secureLog.log(`📡 Richiesta pagina ${Math.floor(offset/pageSize) + 1} (offset: ${offset})`);
    
    try {
      const data = await directFetch(`/table/${tableId}/record`, {
        method: 'GET',
        params
      });
      const pageRecords = data.records || [];
      
      secureLog.log(`📦 Records ricevuti in questa pagina: ${pageRecords.length}`);
      
      if (pageRecords.length === 0) {
        secureLog.log('📋 Nessun record aggiuntivo trovato, fine paginazione');
        break; // Non ci sono più record
      }
      
      allRecords = [...allRecords, ...pageRecords];
      offset += pageRecords.length;
      
      // Se abbiamo ricevuto meno record del pageSize, significa che siamo arrivati alla fine
      if (pageRecords.length < pageSize) {
        secureLog.log(`📋 Fine records raggiunta (ricevuti ${pageRecords.length} < ${pageSize})`);
        break;
      }
      
    } catch (error) {
      secureLog.error('💥 Errore recupero records:', sanitizeErrorMessage(error));
      throw new Error(sanitizeErrorMessage(error));
    }
  }
  
  secureLog.log(`✅ Totale records recuperati: ${allRecords.length}`);
  return allRecords;
};

/**
 * Crea un nuovo record in una tabella con rate limiting
 */
export const createTableRecord = async (tableId: string, fields: any): Promise<any> => {
  if (!isTeableConfigured()) {
    throw new Error('Teable non configurato');
  }

  // Rate limiting per creazione record
  return rateLimiter.execute(`create_${tableId}`, RATE_LIMIT_CONFIGS.CREATE_RECORD, async () => {
    const payload = {
      records: [{ fields }]
    };

    secureLog.log('📝 Creazione record');
    
    try {
      const data = await directFetch(`/table/${tableId}/record`, {
        method: 'POST',
        body: payload
      });

      secureLog.log('✅ Record creato');
      return data.records?.[0];
    } catch (error) {
      secureLog.error('💥 Errore creazione record:', sanitizeErrorMessage(error));
      throw new Error(sanitizeErrorMessage(error));
    }
  });
};

/**
 * Aggiorna un record esistente con rate limiting
 */
export const updateTableRecord = async (tableId: string, recordId: string, fields: any): Promise<any> => {
  if (!isTeableConfigured()) {
    throw new Error('Teable non configurato');
  }

  // Rate limiting per update
  return rateLimiter.execute(`update_${tableId}_${recordId}`, RATE_LIMIT_CONFIGS.MODIFY_RECORD, async () => {
    const payload = {
      records: [{ id: recordId, fields }]
    };

    secureLog.log('📝 Aggiornamento record');
    
    try {
      const data = await directFetch(`/table/${tableId}/record`, {
        method: 'PATCH',
        body: payload
      });

      secureLog.log('✅ Record aggiornato');
      return data.records?.[0];
    } catch (error) {
      secureLog.error('💥 Errore aggiornamento record:', sanitizeErrorMessage(error));
      throw new Error(sanitizeErrorMessage(error));
    }
  });
};

/**
 * Elimina un record con rate limiting
 */
export const deleteTableRecord = async (tableId: string, recordId: string): Promise<boolean> => {
  if (!isTeableConfigured()) {
    throw new Error('Teable non configurato');
  }

  // Rate limiting per delete
  return rateLimiter.execute(`delete_${tableId}_${recordId}`, RATE_LIMIT_CONFIGS.MODIFY_RECORD, async () => {
    const payload = {
      recordIds: [recordId]
    };

    secureLog.log('🗑️ Eliminazione record');
    
    try {
      await directFetch(`/table/${tableId}/record`, {
        method: 'DELETE',
        body: payload
      });

      secureLog.log('✅ Record eliminato');
      return true;
    } catch (error) {
      secureLog.error('💥 Errore eliminazione record:', sanitizeErrorMessage(error));
      throw new Error(sanitizeErrorMessage(error));
    }
  });
};